/**
 * AirlineDocTypeOverridesAdmin Component
 *
 * Purpose: Admin interface for managing airline-specific document type parameter overrides (Tier 2)
 *
 * Features:
 * - View all airline-specific doc type overrides
 * - Create new overrides for specific airline + doc type combinations
 * - Edit existing overrides
 * - Delete overrides (reverts to Tier 1 defaults)
 * - Three-tier parameter system:
 *   Tier 1: doc_types (global defaults)
 *   Tier 2: airline_doc_type_overrides (this panel)
 *   Tier 3: doc_library (individual documents)
 *
 * Sprint: pgvector Enhancement - Airline Document Hierarchy
 * Author: Edward Berger + AI Assistant
 * Date: 2025-11-12
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { AirlineDocTypeOverride, Airline, DocType } from '../../lib/types';
import {
  Settings,
  ChevronDown,
  ChevronUp,
  Edit,
  Plus,
  Loader,
  Trash2,
  Save
} from 'lucide-react';

interface OverrideWithNames extends AirlineDocTypeOverride {
  airline_name?: string;
  airline_code?: string;
  doc_type_label?: string;
  doc_type_code?: string;
}

export const AirlineDocTypeOverridesAdmin: React.FC = () => {
  const [overrides, setOverrides] = useState<OverrideWithNames[]>([]);
  const [airlines, setAirlines] = useState<Airline[]>([]);
  const [docTypes, setDocTypes] = useState<DocType[]>([]);
  const [loading, setLoading] = useState(true);

  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOverride, setSelectedOverride] = useState<OverrideWithNames | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showAdvancedParams, setShowAdvancedParams] = useState(false);

  // New override form
  const [newOverride, setNewOverride] = useState({
    airline_id: '',
    doc_type_id: '',
  });

  // Load data
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    try {
      // Load airlines
      const { data: airlinesData, error: airlinesError } = await supabase
        .from('airlines')
        .select('*')
        .order('code');

      if (airlinesError) {
        console.error('[AirlineDocTypeOverridesAdmin] Load airlines error:', airlinesError);
      } else {
        setAirlines(airlinesData || []);
      }

      // Load doc types
      const { data: docTypesData, error: docTypesError } = await supabase
        .from('doc_types')
        .select('*')
        .order('code');

      if (docTypesError) {
        console.error('[AirlineDocTypeOverridesAdmin] Load doc types error:', docTypesError);
      } else {
        setDocTypes(docTypesData || []);
      }

      // Load overrides
      await loadOverrides(airlinesData || [], docTypesData || []);
    } catch (error) {
      console.error('[AirlineDocTypeOverridesAdmin] Unexpected error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOverrides = async (airlinesData?: Airline[], docTypesData?: DocType[]) => {
    const airlineList = airlinesData || airlines;
    const docTypeList = docTypesData || docTypes;

    const { data, error } = await supabase
      .from('airline_doc_type_overrides')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[AirlineDocTypeOverridesAdmin] Load overrides error:', error);
      alert('Failed to load overrides. Please try again.');
      return;
    }

    // Enrich with names
    const enriched = (data || []).map(override => {
      const airline = airlineList.find(a => a.id === override.airline_id);
      const docType = docTypeList.find(dt => dt.id === override.doc_type_id);
      return {
        ...override,
        airline_name: airline?.name,
        airline_code: airline?.code,
        doc_type_label: docType?.label,
        doc_type_code: docType?.code,
      };
    });

    setOverrides(enriched);
  };

  const handleCreateOverride = async () => {
    if (!newOverride.airline_id || !newOverride.doc_type_id) {
      alert('Please select both an airline and a document type.');
      return;
    }

    // Check if override already exists
    const { data: existing } = await supabase
      .from('airline_doc_type_overrides')
      .select('id')
      .eq('airline_id', newOverride.airline_id)
      .eq('doc_type_id', newOverride.doc_type_id)
      .single();

    if (existing) {
      alert('An override for this airline and document type already exists.');
      return;
    }

    const { error } = await supabase
      .from('airline_doc_type_overrides')
      .insert({
        airline_id: newOverride.airline_id,
        doc_type_id: newOverride.doc_type_id,
      });

    if (error) {
      console.error('[AirlineDocTypeOverridesAdmin] Create error:', error);
      alert('Failed to create override.');
      return;
    }

    alert('Override created successfully!');
    setShowCreateModal(false);
    setNewOverride({ airline_id: '', doc_type_id: '' });
    loadOverrides();
  };

  const handleDeleteOverride = async (id: string) => {
    if (!confirm('Delete this override? The airline will revert to using Tier 1 (doc type) defaults.')) {
      return;
    }

    const { error } = await supabase
      .from('airline_doc_type_overrides')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('[AirlineDocTypeOverridesAdmin] Delete error:', error);
      alert('Failed to delete override.');
      return;
    }

    alert('Override deleted successfully!');
    loadOverrides();
  };

  const handleUpdateOverride = async () => {
    if (!selectedOverride) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('airline_doc_type_overrides')
        .update({
          type_boost_override: selectedOverride.type_boost_override,
          k_override: selectedOverride.k_override,
          score_threshold_override: selectedOverride.score_threshold_override,
          alpha_hybrid_override: selectedOverride.alpha_hybrid_override,
          lambda_mmr_override: selectedOverride.lambda_mmr_override,
          field_weight_title_override: selectedOverride.field_weight_title_override,
          field_weight_body_override: selectedOverride.field_weight_body_override,
          field_weight_caption_override: selectedOverride.field_weight_caption_override,
          recency_decay_override: selectedOverride.recency_decay_override,
          assessment_boost_override: selectedOverride.assessment_boost_override,
        })
        .eq('id', selectedOverride.id);

      if (error) {
        console.error('[AirlineDocTypeOverridesAdmin] Update error:', error);
        alert('Failed to update override.');
        return;
      }

      alert('Override updated successfully!');
      setShowDetailsModal(false);
      await loadOverrides();
    } catch (error) {
      console.error('[AirlineDocTypeOverridesAdmin] Unexpected error:', error);
      alert('An unexpected error occurred while saving.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-navy">Airline Doc Type Overrides</h1>
          <p className="text-steel mt-1">
            Manage airline-specific retrieval parameter overrides (Tier 2)
          </p>
        </div>

        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orca-elevate text-white rounded-lg hover:bg-orca-elevate/90 transition"
        >
          <Plus size={20} />
          Create Override
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-sm">
        <p className="font-semibold text-blue-900 mb-1">Three-Tier Parameter System</p>
        <p className="text-blue-800">
          <strong>Tier 1:</strong> Doc type defaults (DocTypesAdmin) →
          <strong className="text-orca-elevate"> Tier 2: Airline-specific overrides (this panel)</strong> →
          <strong> Tier 3:</strong> Individual document overrides (DocumentsAdmin)
        </p>
      </div>

      {/* Overrides List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader className="animate-spin text-orca-elevate" size={32} />
        </div>
      ) : overrides.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <Settings className="mx-auto text-steel/50" size={48} />
          <p className="text-steel mt-4">No airline-specific overrides configured.</p>
          <p className="text-steel/70 text-sm mt-2">All airlines are using Tier 1 (doc type) defaults.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-x-auto">
          <table className="w-full">
            <thead className="bg-navy text-white">
              <tr>
                <th className="px-4 py-3 text-left text-sm">Airline</th>
                <th className="px-4 py-3 text-left text-sm">Doc Type</th>
                <th className="px-4 py-3 text-center text-sm">Type Boost</th>
                <th className="px-4 py-3 text-center text-sm">Top-K</th>
                <th className="px-4 py-3 text-center text-sm">Score Threshold</th>
                <th className="px-4 py-3 text-center text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((override) => (
                <tr key={override.id} className="border-b border-steel/20 hover:bg-white transition">
                  <td className="px-4 py-3 text-sm">
                    <div>
                      <p className="font-semibold">{override.airline_name}</p>
                      <p className="text-xs text-steel">{override.airline_code}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div>
                      <p className="font-semibold">{override.doc_type_label}</p>
                      <p className="text-xs text-steel font-mono">{override.doc_type_code}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center text-sm">
                    {override.type_boost_override ?? <span className="text-steel/50">-</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-sm">
                    {override.k_override ?? <span className="text-steel/50">-</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-sm">
                    {override.score_threshold_override ?? <span className="text-steel/50">-</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={() => {
                          setSelectedOverride(override);
                          setShowDetailsModal(true);
                        }}
                        className="p-1 hover:bg-orca-elevate/10 rounded transition"
                        title="Edit"
                      >
                        <Edit size={16} className="text-orca-elevate" />
                      </button>
                      <button
                        onClick={() => handleDeleteOverride(override.id)}
                        className="p-1 hover:bg-red-100 rounded"
                        title="Delete Override"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-navy mb-4">Create Airline Override</h2>

            <div className="space-y-3">
              <div>
                <label className="text-sm text-steel block mb-1">Airline</label>
                <select
                  value={newOverride.airline_id}
                  onChange={(e) => setNewOverride({ ...newOverride, airline_id: e.target.value })}
                  className="w-full px-3 py-2 border border-steel/30 rounded text-sm"
                >
                  <option value="">Select Airline</option>
                  {airlines.map(airline => (
                    <option key={airline.id} value={airline.id}>
                      {airline.name} ({airline.code})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-steel block mb-1">Document Type</label>
                <select
                  value={newOverride.doc_type_id}
                  onChange={(e) => setNewOverride({ ...newOverride, doc_type_id: e.target.value })}
                  className="w-full px-3 py-2 border border-steel/30 rounded text-sm"
                >
                  <option value="">Select Document Type</option>
                  {docTypes.map(docType => (
                    <option key={docType.id} value={docType.id}>
                      {docType.label} ({docType.code})
                    </option>
                  ))}
                </select>
              </div>

              <p className="text-xs text-steel bg-white p-2 rounded border border-steel/20">
                After creating the override, you can edit the parameters to customize retrieval behavior for this airline + doc type combination.
              </p>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewOverride({ airline_id: '', doc_type_id: '' });
                }}
                className="px-3 py-1.5 border border-steel/30 rounded hover:bg-steel/10 transition text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOverride}
                className="px-3 py-1.5 bg-orca-elevate text-white rounded hover:bg-orca-elevate/90 transition text-xs flex items-center gap-1"
              >
                <Plus size={14} />
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showDetailsModal && selectedOverride && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Fixed Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-steel/20">
              <div>
                <h2 className="text-xl font-bold text-navy">{selectedOverride.airline_name}</h2>
                <p className="text-xs text-steel">{selectedOverride.doc_type_label} Overrides</p>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              <div className="space-y-3">
                {/* Core Parameters */}
                <div className="border-t border-steel/20 pt-2 mt-2">
                  <h3 className="text-sm font-semibold text-navy mb-2 flex items-center gap-1">
                    <Settings size={14} />
                    Core Retrieval Parameters
                  </h3>

                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <label className="text-steel block mb-0.5">Type Boost Override</label>
                      <input
                        type="number"
                        step="0.1"
                        value={selectedOverride.type_boost_override ?? ''}
                        onChange={(e) => setSelectedOverride({
                          ...selectedOverride,
                          type_boost_override: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="Leave empty for default"
                        className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-steel block mb-0.5">Top-K Override</label>
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={selectedOverride.k_override ?? ''}
                        onChange={(e) => setSelectedOverride({
                          ...selectedOverride,
                          k_override: e.target.value ? parseInt(e.target.value) : undefined
                        })}
                        placeholder="Leave empty for default"
                        className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-steel block mb-0.5">Score Threshold Override</label>
                      <input
                        type="number"
                        step="0.05"
                        min="0"
                        max="1"
                        value={selectedOverride.score_threshold_override ?? ''}
                        onChange={(e) => setSelectedOverride({
                          ...selectedOverride,
                          score_threshold_override: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="Leave empty for default"
                        className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-steel block mb-0.5">Alpha Hybrid Override</label>
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="1"
                        value={selectedOverride.alpha_hybrid_override ?? ''}
                        onChange={(e) => setSelectedOverride({
                          ...selectedOverride,
                          alpha_hybrid_override: e.target.value ? parseFloat(e.target.value) : undefined
                        })}
                        placeholder="Leave empty for default"
                        className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                      />
                    </div>
                  </div>
                </div>

                {/* Advanced Parameters */}
                <div className="border-t border-steel/20 pt-2 mt-2">
                  <button
                    onClick={() => setShowAdvancedParams(!showAdvancedParams)}
                    className="flex items-center justify-between w-full text-left mb-1 hover:bg-steel/5 p-1 rounded transition"
                  >
                    <h3 className="text-sm font-semibold text-navy flex items-center gap-1">
                      <Settings size={14} />
                      Advanced Parameters
                    </h3>
                    {showAdvancedParams ? (
                      <ChevronUp size={16} className="text-steel" />
                    ) : (
                      <ChevronDown size={16} className="text-steel" />
                    )}
                  </button>

                  {showAdvancedParams && (
                    <div className="space-y-2 bg-white p-2 rounded border border-steel/20">
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <label className="text-steel block mb-0.5">Lambda MMR Override</label>
                          <input
                            type="number"
                            step="0.1"
                            min="0"
                            max="1"
                            value={selectedOverride.lambda_mmr_override ?? ''}
                            onChange={(e) => setSelectedOverride({
                              ...selectedOverride,
                              lambda_mmr_override: e.target.value ? parseFloat(e.target.value) : undefined
                            })}
                            placeholder="Leave empty for default"
                            className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                          />
                        </div>
                        <div>
                          <label className="text-steel block mb-0.5">Weight: Title Override</label>
                          <input
                            type="number"
                            step="0.1"
                            value={selectedOverride.field_weight_title_override ?? ''}
                            onChange={(e) => setSelectedOverride({
                              ...selectedOverride,
                              field_weight_title_override: e.target.value ? parseFloat(e.target.value) : undefined
                            })}
                            placeholder="Leave empty for default"
                            className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                          />
                        </div>
                        <div>
                          <label className="text-steel block mb-0.5">Weight: Body Override</label>
                          <input
                            type="number"
                            step="0.1"
                            value={selectedOverride.field_weight_body_override ?? ''}
                            onChange={(e) => setSelectedOverride({
                              ...selectedOverride,
                              field_weight_body_override: e.target.value ? parseFloat(e.target.value) : undefined
                            })}
                            placeholder="Leave empty for default"
                            className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                          />
                        </div>
                        <div>
                          <label className="text-steel block mb-0.5">Weight: Caption Override</label>
                          <input
                            type="number"
                            step="0.1"
                            value={selectedOverride.field_weight_caption_override ?? ''}
                            onChange={(e) => setSelectedOverride({
                              ...selectedOverride,
                              field_weight_caption_override: e.target.value ? parseFloat(e.target.value) : undefined
                            })}
                            placeholder="Leave empty for default"
                            className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                          />
                        </div>
                        <div>
                          <label className="text-steel block mb-0.5">Recency Decay Override</label>
                          <input
                            type="number"
                            step="0.0001"
                            min="0"
                            value={selectedOverride.recency_decay_override ?? ''}
                            onChange={(e) => setSelectedOverride({
                              ...selectedOverride,
                              recency_decay_override: e.target.value ? parseFloat(e.target.value) : undefined
                            })}
                            placeholder="Leave empty for default"
                            className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                          />
                        </div>
                        <div>
                          <label className="text-steel block mb-0.5">Assessment Boost Override</label>
                          <input
                            type="number"
                            step="0.1"
                            value={selectedOverride.assessment_boost_override ?? ''}
                            onChange={(e) => setSelectedOverride({
                              ...selectedOverride,
                              assessment_boost_override: e.target.value ? parseFloat(e.target.value) : undefined
                            })}
                            placeholder="Leave empty for default"
                            className="w-full px-2 py-1 border border-steel/30 rounded text-xs"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="border-t border-steel/20 px-6 py-3 bg-white flex justify-end gap-2">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-3 py-1.5 border border-steel/30 rounded hover:bg-steel/10 transition text-xs"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateOverride}
                className="px-3 py-1.5 bg-orca-elevate text-white rounded hover:bg-orca-elevate/90 transition text-xs flex items-center gap-1"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader size={14} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save size={14} />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AirlineDocTypeOverridesAdmin;
